<template>
  <TheLayout :use-header="shouldUseHeader">
    <template v-if="isAppReady">
      <router-view />
      <ModalContainer />
      <AlertContainer />
    </template>
    <div v-else class="t-flex t-h-full t-items-center t-justify-center">
      <TProgressCircular v-if="shouldShowLoader" :indeterminate="true" color="primary" />
    </div>
  </TheLayout>
</template>

<script>
import { defineComponent, provide, onUnmounted, computed } from 'vue-demi';
import useAppFacade from '@/platform/composables/useAppFacade.js';
import usePopState from '@/platform/composables/usePopState.js';
import bindLegacyConnectedEvents from '@/platform/utils/legacy-connected-events';

import { provideTotango } from '@/platform/composables/useTotango.js';
import { TheLayout, TProgressCircular } from '@teamwork/ui-kit';
import AlertContainer from '@/platform/components/AlertContainer.vue';
import ModalContainer from '@/platform/components/ModalContainer.vue';
import api from '@teamwork/fetcher';
import { provideAxios, provideOptimisticUpdates, provideRealTimeUpdates } from '@teamwork/use';
import { provideLaunchDarkly } from '@/platform/composables/useLaunchDarkly';

export default defineComponent({
  name: 'App',
  components: {
    AlertContainer,
    ModalContainer,
    TheLayout,
    TProgressCircular,
  },
  setup(props, context) {
    provideLaunchDarkly();
    provideAxios(api);
    provideOptimisticUpdates();
    provideRealTimeUpdates();

    const appFacade = useAppFacade({ context });
    appFacade.legacyConnector.check();
    appFacade.userContext.fetchCurrentUser();

    appFacade.logger.logInfo('Providing App Facade to component tree');
    appFacade.logger.logObject(appFacade);
    provide('appFacade', appFacade);

    const unbindEvents = bindLegacyConnectedEvents(window, document.body);
    onUnmounted(unbindEvents);

    // Need this for provideTotango
    const {
      accountContext: { account },
      userContext: { user },
    } = appFacade;

    // Totango event tracking
    provideTotango({ account, user });

    const shouldUseHeader = computed(() => !appFacade.legacyConnector.isLegacyConnected.value);
    const shouldShowLoader = computed(() => !appFacade.modalManager.isModalRoute.value);

    // when back button triggers iframe popstate
    // see: https://stackoverflow.com/questions/60549567/chrome-80-duplicating-history-entries-if-iframe-present-on-page
    usePopState({
      w: window,
      isLegacyConnected: appFacade.legacyConnector.isLegacyConnected.value,
    });

    return {
      shouldShowLoader,
      shouldUseHeader,
      isAppReady: appFacade.isAppReady,
    };
  },
});
</script>
<style>
/* TODO: This removes all backgrounds in the app */
.theme--light.v-application,
.theme--dark.v-application {
  background: none !important;
}
</style>
